import { useEffect, useState } from "react";
import { toast } from "sonner";
import { updateSceneModes } from "@/lib/api";
import SceneModePicker, {
  SCENE_MODE_LABELS,
  SCENE_MODE_OPTIONS,
} from "./SceneModePicker";

/**
 * SceneCompositionCard — summary panel above the "▸ Render full video"
 * button. Lets the user batch-apply a mode to every scene OR toggle each
 * scene individually, and shows a real-time cost-impact summary.
 *
 * Default for every scene is "still_static" (cheapest). Video mode incurs
 * fal.ai cost per clip.
 */
export default function SceneCompositionCard({
  doc,
  jobId,
  scenes,
  selectedProvider,
  videoProviders,
  disabled,
  onChanged,
}) {
  const total = Array.isArray(scenes) ? scenes.length : 0;
  const [modes, setModes] = useState(() => {
    const docModes = Array.isArray(doc?.scene_modes) ? doc.scene_modes : [];
    return Array.from({ length: total }, (_, i) =>
      docModes[i] && SCENE_MODE_LABELS[docModes[i]] ? docModes[i] : "still_static",
    );
  });
  const [saving, setSaving] = useState(false);

  // Re-hydrate when doc.scene_modes or scene count changes from the server.
  useEffect(() => {
    const docModes = Array.isArray(doc?.scene_modes) ? doc.scene_modes : [];
    setModes(
      Array.from({ length: total }, (_, i) =>
        docModes[i] && SCENE_MODE_LABELS[docModes[i]] ? docModes[i] : "still_static",
      ),
    );
  }, [doc?.scene_modes, total]);

  if (total === 0) return null;

  const docModesCurrent = (i) => {
    const docModes = Array.isArray(doc?.scene_modes) ? doc.scene_modes : [];
    const m = docModes[i];
    return m && SCENE_MODE_LABELS[m] ? m : "still_static";
  };
  const dirtyIndices = modes
    .map((m, i) => (m !== docModesCurrent(i) ? i : -1))
    .filter((i) => i >= 0);
  const isDirty = dirtyIndices.length > 0;

  const counts = SCENE_MODE_OPTIONS.reduce((acc, opt) => {
    acc[opt.id] = modes.filter((m) => m === opt.id).length;
    return acc;
  }, {});

  const providerCfg = videoProviders?.find((p) => p.id === selectedProvider);
  const costPerVideoClip = providerCfg?.cost_per_clip_usd || 0;
  const videoCount = counts.video || 0;
  const estCost = (videoCount * costPerVideoClip).toFixed(2);

  const applyAll = (id) => setModes(Array.from({ length: total }, () => id));
  const setOne = (idx, id) =>
    setModes((prev) => prev.map((m, i) => (i === idx ? id : m)));

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateSceneModes(jobId, modes);
      if (res.changed_indices?.length) {
        toast.success(
          `Updated ${res.changed_indices.length} scene(s) · ${res.clips_invalidated} cached clip(s) cleared`,
        );
      } else {
        toast.success("Scene modes saved");
      }
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save scene modes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-zinc-800 rounded-sm bg-black p-5 mb-4 text-left"
      data-testid="scene-composition-card"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-yellow-400">
          ⚏ Scene composition · {total} scene{total === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
            Apply to all
          </span>
          {SCENE_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => applyAll(opt.id)}
              disabled={disabled || saving}
              data-testid={`scene-mode-apply-all-${opt.id}`}
              className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 border border-zinc-800 hover:text-yellow-300 hover:border-yellow-400/40 px-2 py-1 rounded-sm transition-colors disabled:opacity-40"
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {SCENE_MODE_OPTIONS.map((opt) => (
          <div
            key={opt.id}
            className="border border-zinc-900 rounded-sm py-2 px-3"
            data-testid={`scene-mode-count-${opt.id}`}
          >
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">
              {opt.label}
            </div>
            <div className="font-mono text-base text-white">
              {counts[opt.id] || 0}
              <span className="text-zinc-500 text-xs"> / {total}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Per-scene picker grid */}
      <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="scene-mode-list">
        {scenes.map((desc, i) => {
          const wasChanged = dirtyIndices.includes(i);
          return (
            <div
              key={i}
              className={
                "flex items-center gap-3 px-2 py-1.5 rounded-sm border " +
                (wasChanged
                  ? "border-yellow-400/40 bg-yellow-400/5"
                  : "border-zinc-900 bg-transparent")
              }
              data-testid={`scene-mode-row-${i}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 shrink-0 w-14">
                #{String(i + 1).padStart(2, "0")}
              </div>
              <div className="text-xs text-zinc-300 flex-1 truncate" title={desc}>
                {desc}
              </div>
              <SceneModePicker
                value={modes[i] || "still_static"}
                onChange={(m) => setOne(i, m)}
                disabled={disabled || saving}
                sceneIdx={i}
                compact
              />
            </div>
          );
        })}
      </div>

      {/* Footer: cost estimate + save */}
      <div className="mt-4 pt-3 border-t border-zinc-900 flex items-center justify-between gap-3 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
          fal.ai cost estimate:{" "}
          <span
            className={videoCount > 0 ? "text-yellow-300" : "text-emerald-300"}
            data-testid="scene-mode-cost-estimate"
          >
            {videoCount > 0 ? `~$${estCost}` : "$0 (all stills)"}
          </span>
          <span className="text-zinc-500 normal-case tracking-normal ml-2">
            · {videoCount} video clip{videoCount === 1 ? "" : "s"} @ ~${costPerVideoClip || 0}/clip
          </span>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!isDirty || saving || disabled}
          data-testid="scene-mode-save-btn"
          className="font-mono text-[10px] uppercase tracking-[0.2em] bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-sm hover:bg-yellow-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          {saving ? (
            <>
              <span className="inline-block w-2.5 h-2.5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              Saving…
            </>
          ) : (
            <>✓ Save · {dirtyIndices.length || 0} changed</>
          )}
        </button>
      </div>
    </div>
  );
}
